/**
 * `@4f/frontend` API-layer errors. Every failure the client raises extends
 * `AppError` (stable `code`), never a bare `Error` — matching the repo-wide
 * convention. These represent TRANSPORT / boundary failures (network down,
 * non-2xx with no envelope, malformed body); an expected domain outcome the
 * edge returns as `{ ok: false, error }` stays an `ApiError`, not one of these.
 */
import { AppError } from '@4f/shared';

/** The network request never completed (offline, DNS, CORS, aborted mid-flight). */
export class NetworkError extends AppError {
  readonly code = 'network_error';

  constructor(cause: unknown) {
    super('The request could not be completed. Check your connection and try again.', { cause });
  }
}

/**
 * A response arrived but its body did not match the expected envelope / schema
 * (a contract drift or a proxy error page). Distinct from a well-formed domain
 * error so the UI can tell "the server said no" from "the server spoke garbage".
 */
export class MalformedResponseError extends AppError {
  readonly code = 'malformed_response';

  constructor(detail: string) {
    super(`The server returned an unexpected response: ${detail}`);
  }
}

/**
 * Is this failure TRANSPORT-class — "we never got an answer" — as opposed to a
 * domain outcome the edge deliberately returned?
 *
 * The two codes below are the only failures that carry no HTTP status: nothing
 * reached the edge ({@link NetworkError}), or something that is not the edge
 * answered ({@link MalformedResponseError}) — an ingress load-balancer 503 with a
 * `text/plain` body being the case that motivated this.
 *
 * It lives HERE, next to the codes it tests, because two callers already need the
 * same question answered and they punish a wrong answer in opposite directions:
 * `chat-delivery.ts` would DELETE a queued message by reading a blip as a real
 * "no", and `session.ts` would throw away a still-valid access token and drop the
 * visitor at a login form that cannot work. A second inline copy of the code pair
 * is exactly the drift that `category-aliases.ts` documents on the backend.
 *
 * Structural on `code` rather than `instanceof`, because both callers receive an
 * `ApiError` that has already crossed the `Result` boundary — the class is gone by
 * then, the code is what survives.
 */
export function isTransportFailure(error: { readonly code: string }): boolean {
  return error.code === 'network_error' || error.code === 'malformed_response';
}
